import React from 'react'
import PropTypes from 'prop-types'
import { connect } from 'react-redux'
import FontAwesome from 'react-fontawesome'
import hotkeys from 'hotkeys-js'
import { get } from 'lodash'

import GuideActions from 'actions/guides'
import SimpleModal from 'components/Elements/SimpleModal'
import { Spinner } from 'components/Elements/Loading'
import log, { error } from 'utils/logger' // eslint-disable-line

const isFunction = (object) => !!(object && typeof object === 'function')

import 'stylesheets/guides.scss'

const
  importGuide = (guideName, cb) => {
    import(`components/GuideViewer/guides/${guideName}`).then(cb)
  },
  GUIDES = {
    'meta': (cb) => importGuide('meta', cb),
    'dashboards': (cb) => importGuide('dashboards', cb),
    'feeds': (cb) => importGuide('feeds', cb),
    'triggers': (cb) => importGuide('triggers', cb),
    'python': (cb) => importGuide('python', cb),
    'arduino': (cb) => importGuide('arduino', cb),
    'public_keys': (cb) => importGuide('public_keys', cb),
    'access_tokens': (cb) => importGuide('access_tokens', cb)
  }

class GuideViewer extends React.Component {
  static propTypes = {
    show: PropTypes.bool
  }

  constructor(...args) {
    super(...args)

    this.state = {
      guide: null,
      loading: false,
    }
  }

  loadGuide() {
    const guideLoaderFunc = GUIDES[this.props.name]
    // No Guide by this name? Error.
    if(!guideLoaderFunc) { return error("NO GUIDE WITH NAME", this.props.name) }
    // Not a loader function? Must be the guide itself
    if(!isFunction(guideLoaderFunc)) {
      return this.setState({ guide: guideLoaderFunc })
    }

    // Prepare to show a spinner if load takes too long
    const _preloadTimeout = setTimeout(() => {
      this.setState({ guide: null, loading: true })
    }, 100)

    // Start the load
    guideLoaderFunc(guide => {
      log('loaded guide')
      clearTimeout(_preloadTimeout) // Clear the spinner
      this.setState({ guide, loading: false })
    })
  }

  componentDidMount() {
    if (this.props.name) {
      this.loadGuide()
    }
  }

  componentDidUpdate(prevProps) {
    if (this.props.name && this.props.name !== prevProps.name) {
      this.loadGuide()
    }

    // becoming visible
    if (!prevProps.show && this.props.show) {
      hotkeys('right', this.nextPage)
      hotkeys('left', this.prevPage)

    // becoming invisible
    } else if (prevProps.show && !this.props.show) {
      hotkeys.unbind('right')
      hotkeys.unbind('left')
    }
  }

  prevPage = () => {
    const previousPage = this.props.page - 1

    if (previousPage < 0) { return }

    this.props.showGuidePage(previousPage)
  }

  nextPage = () => {
    const
      nextPage = this.props.page + 1,
      guideLength = get(this, "state.guide.pages.length", 0)

    if (nextPage >= guideLength) { return }

    this.props.showGuidePage(nextPage)
  }

  renderPage() {
    const
      { name, page } = this.props,
      { guide } = this.state

    if (guide && !isNaN(page)) {
      const GuidePage = guide.pages[page]
      if (typeof GuidePage === 'string') {
        return <div dangerouslySetInnerHTML={{__html: GuidePage}} />
      } else if (GuidePage) {
        return <GuidePage />
      }
    } else {
      return (
        <>
          <p>unknown content:</p>
          <pre>{JSON.stringify({ name, page }, null, '  ')}</pre>
        </>
      )
    }
  }

  renderLoading() {
    return (
      <div className="placeholder">
        <Spinner blue />
      </div>
    )
  }

  renderFooter() {
    const
      page = this.props.page + 1,
      isMetaGuide = this.props.name === "meta",
      length = get(this, "state.guide.pages.length", 0),
      hasPrev = page > 1,
      hasNext = page < length,
      counter = length > 1 && `${page} of ${length}`,
      otherGuidesClass = "btn left" + ( isMetaGuide ? " disabled" : "" ),
      prevClass = "btn right " + ( hasPrev ? "blue" : "disabled" ),
      nextClass = "btn right " + ( hasNext ? "blue" : "disabled" )

    return (
      <div className="guide-buttons">
        { counter &&
          <span className="page-count">{ counter }</span>
        }
        <a disabled={ isMetaGuide } className={ otherGuidesClass } onClick={ this.props.showMetaGuide }>See other guides</a>
        <a disabled={ !hasNext } className={ nextClass } onClick={this.nextPage}>Next <FontAwesome name="chevron-right" /></a>
        <a disabled={ !hasPrev } className={ prevClass } onClick={this.prevPage}><FontAwesome name="chevron-left" /> Prev</a>
      </div>
    )
  }

  render() {
    const { guide } = this.state

    if (guide && guide.Walkthrough) {
      return <guide.Walkthrough />
    }

    const title = guide && guide.title || "Loading..."
    return (
      <SimpleModal show={ this.props.show } id='guides-modal' title={ title } onHide={ this.props.hide } footer={ this.renderFooter() }>
        { guide && this.renderPage() }
        { this.state.loading && this.renderLoading() }
      </SimpleModal>
    )
  }
}

const
  mapState = state => (state.guides || {}), // { show, name, page }

  mapDispatch = dispatch => ({
    hide: () => dispatch(GuideActions.hideGuide()),
    showMetaGuide: () => dispatch(GuideActions.showGuide('meta')),
    showGuidePage: (page) => dispatch(GuideActions.showGuidePage(page)),
  })

export default connect(mapState, mapDispatch)(GuideViewer)
